/**
 * Endpoints de **Gestión → Administración**: consumo por cliente y canjes
 * masivos de notas de venta.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { env } from '@/config/env';
import { apiClient } from '@/lib/http/api-client';
import { getSessionProvider } from '@/lib/http/session-provider';

import type {
  CatalogosCanjeRespuesta,
  CanjeMasivo,
  ConsumoRespuesta,
  DetalleConsumoRespuesta,
  FiltrosNotas,
  LiquidacionNotasRespuesta,
  NotasRespuesta,
  PeticionLiquidarNotas,
} from './administracion-canjes.types';

const CLAVE = 'gestion';

/** Convierte los filtros en cadena de consulta. */
function cadenaDe(filtros: { [clave: string]: string | number | undefined }): string {
  const parametros = new URLSearchParams();

  for (const [clave, valor] of Object.entries(filtros)) {
    if (valor === undefined || valor === null || valor === '') continue;

    parametros.set(clave, String(valor));
  }

  const cadena = parametros.toString();

  return cadena ? `?${cadena}` : '';
}

export const administracionKeys = {
  consumo: (documento: string, desde: string, hasta: string) =>
    [CLAVE, 'administracion', 'consumo', documento, desde, hasta] as const,
  consumoDetalle: (documento: string, desde: string, hasta: string) =>
    [CLAVE, 'administracion', 'consumo-detalle', documento, desde, hasta] as const,
  notas: (filtros: FiltrosNotas) => [CLAVE, 'administracion', 'canjes-notas', filtros] as const,
  catalogosCanje: (tipo: string) => [CLAVE, 'administracion', 'canjes-catalogos', tipo] as const,
  historial: () => [CLAVE, 'administracion', 'canjes-historial'] as const,
};

export const administracionApi = {
  consumo: (
    documento: string,
    desde: string,
    hasta: string,
    signal?: AbortSignal,
  ): Promise<ConsumoRespuesta> =>
    apiClient.get<ConsumoRespuesta>(
      `/api/gestion/administracion/consumo${cadenaDe({ documento, desde, hasta })}`,
      { signal },
    ),

  consumoDetalle: (
    documento: string,
    desde: string,
    hasta: string,
    signal?: AbortSignal,
  ): Promise<DetalleConsumoRespuesta> =>
    apiClient.get<DetalleConsumoRespuesta>(
      `/api/gestion/administracion/consumo/detalle${cadenaDe({ documento, desde, hasta })}`,
      { signal },
    ),

  /**
   * Descarga el consumo en Excel. Igual que los PDF: la API exige el token de
   * sesión, así que se pide con `fetch` y se guarda el archivo.
   */
  exportarConsumo: async (documento: string, desde: string, hasta: string): Promise<void> => {
    const token = getSessionProvider()?.getAccessToken();
    const respuesta = await fetch(
      `${env.apiUrl}/api/gestion/administracion/consumo/exportar${cadenaDe({ documento, desde, hasta })}`,
      { headers: token ? { Authorization: `Bearer ${token}` } : undefined },
    );

    if (!respuesta.ok) {
      throw new Error(`No se pudo exportar el consumo (HTTP ${respuesta.status}).`);
    }

    const blob = await respuesta.blob();
    const url = URL.createObjectURL(blob);
    const enlace = document.createElement('a');

    enlace.href = url;
    enlace.download = `consumo-${documento}${desde ? `-${desde}` : ''}.xlsx`;
    document.body.appendChild(enlace);
    enlace.click();
    enlace.remove();
    URL.revokeObjectURL(url);
  },

  notas: (filtros: FiltrosNotas, signal?: AbortSignal): Promise<NotasRespuesta> =>
    apiClient.get<NotasRespuesta>(
      `/api/gestion/administracion/canjes-notas${cadenaDe({ ...filtros })}`,
      { signal },
    ),

  catalogosCanje: (tipo: string, signal?: AbortSignal): Promise<CatalogosCanjeRespuesta> =>
    apiClient.get<CatalogosCanjeRespuesta>(
      `/api/gestion/administracion/canjes-notas/catalogos${cadenaDe({ tipo })}`,
      { signal },
    ),

  liquidar: (datos: PeticionLiquidarNotas): Promise<LiquidacionNotasRespuesta> =>
    apiClient.post<LiquidacionNotasRespuesta>(
      '/api/gestion/administracion/canjes-notas/liquidar',
      { body: datos },
    ),

  historial: (signal?: AbortSignal): Promise<{ canjes: CanjeMasivo[]; total: number }> =>
    apiClient.get<{ canjes: CanjeMasivo[]; total: number }>(
      '/api/gestion/administracion/canjes-notas/historial',
      { signal },
    ),

  anularCanje: (id: number): Promise<{ canje_id: number; notas_liberadas: number; mensaje: string }> =>
    apiClient.post<{ canje_id: number; notas_liberadas: number; mensaje: string }>(
      `/api/gestion/administracion/canjes-notas/${id}/anular`,
      { body: {} },
    ),
};

/** Consumo de un cliente por documento y rango. */
export function useConsumo(documento: string, desde: string, hasta: string, activo: boolean) {
  return useQuery({
    queryKey: administracionKeys.consumo(documento, desde, hasta),
    queryFn: ({ signal }) => administracionApi.consumo(documento, desde, hasta, signal),
    enabled: activo,
    retry: false,
  });
}

/** Detalle del consumo del cliente (POS y administración). */
export function useConsumoDetalle(documento: string, desde: string, hasta: string, activo: boolean) {
  return useQuery({
    queryKey: administracionKeys.consumoDetalle(documento, desde, hasta),
    queryFn: ({ signal }) => administracionApi.consumoDetalle(documento, desde, hasta, signal),
    enabled: activo,
    retry: false,
  });
}

/** Notas de venta del rango. */
export function useNotas(filtros: FiltrosNotas) {
  return useQuery({
    queryKey: administracionKeys.notas(filtros),
    queryFn: ({ signal }) => administracionApi.notas(filtros, signal),
    staleTime: 15_000,
  });
}

/** Catálogos del canje (series, clientes y productos). */
export function useCatalogosCanje(tipo: string) {
  return useQuery({
    queryKey: administracionKeys.catalogosCanje(tipo),
    queryFn: ({ signal }) => administracionApi.catalogosCanje(tipo, signal),
    staleTime: 60_000,
  });
}

/** Histórico de canjes masivos. */
export function useHistorialCanjes() {
  return useQuery({
    queryKey: administracionKeys.historial(),
    queryFn: ({ signal }) => administracionApi.historial(signal),
    staleTime: 15_000,
  });
}

/** Liquida las notas seleccionadas. */
export function useLiquidarNotas() {
  const cliente = useQueryClient();

  return useMutation({
    mutationFn: (datos: PeticionLiquidarNotas) => administracionApi.liquidar(datos),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: [CLAVE, 'administracion'] });
    },
  });
}

/** Anula un canje masivo. */
export function useAnularCanje() {
  const cliente = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => administracionApi.anularCanje(id),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: [CLAVE, 'administracion'] });
    },
  });
}
